import React from 'react';
import { PleriClient } from '../../../components/Pleri/client';
import type { ContextItem } from '../../../components/Pleri/store/chatStore';
import type { WidgetConfig } from '../Widget/Widget';
import { useDashboardContext } from './DashboardContext';
import { useLocation } from '@tanstack/react-router';
import { UniversalDashboardRenderer } from './UniversalDashboardRenderer';

import { useTranslation } from 'react-i18next';

interface DashboardChatOptions {
  componentName: string;
  componentData?: Record<string, unknown>;
  contextType?: 'widget' | 'chart' | 'table' | 'metric';
  config?: WidgetConfig;
  // dashboardId is now provided by context, no longer needed as a prop
}

// Reusable function to create widget context items
const createWidgetContextItems = (
  componentName: string,
  contextType: string,
  config?: WidgetConfig,
  dashboardContext?: {
    dashboardId: string | null;
    dashboardTitle?: string;
    dashboardScope?: string;
  },
): ContextItem[] => {
  const contextItems: ContextItem[] = [];

  // Add dashboard context if available
  if (dashboardContext?.dashboardId) {
    const displayName =
      dashboardContext.dashboardTitle ||
      `Dashboard ${dashboardContext.dashboardId}`;
    contextItems.push({
      id: `dashboard-${dashboardContext.dashboardId}-${Date.now()}`,
      type: 'dashboard',
      prompt: `Dashboard ID: ${dashboardContext.dashboardId}${dashboardContext.dashboardTitle ? `\nDashboard Title: ${dashboardContext.dashboardTitle}` : ''}${dashboardContext.dashboardScope ? `\nDashboard Scope: ${dashboardContext.dashboardScope}` : ''}`,
      display: displayName,
    });
  }

  // Widget context is now included in the widget config preview, so we don't need a separate badge

  // Add widget configuration if available
  if (config) {
    const title =
      (config.props.title as string) || (config.component as string);
    contextItems.push({
      id: `config-${componentName}-${Date.now()}`,
      type: 'widget_config',
      prompt: `Widget configuration for ${title}:\n\`\`\`widget\n${JSON.stringify(config, null, 2)}\n\`\`\``,
      display: title,
    });
  }

  return contextItems;
};

// Safe hook to get location, with fallback when used outside router context
const useSafeLocation = () => {
  try {
    return useLocation();
  } catch (error) {
    // Return fallback location when outside router context
    return {
      pathname: '',
      search: '',
      hash: '',
      state: null,
    };
  }
};

export const useDashboardChat = ({
  componentName,
  contextType = 'widget',
  config,
}: DashboardChatOptions) => {
  const { t } = useTranslation('pleri/dashboard');
  // Get dashboard context using the hook
  const dashboardContext = useDashboardContext();
  const location = useSafeLocation();
  const normalizedPathname = location.pathname.replace(/^\/pleri/, '');

  // Check if we're on a dashboard page
  const isDashboardPage =
    normalizedPathname.startsWith('/dashboard/') &&
    normalizedPathname !== '/dashboard/' &&
    dashboardContext?.dashboardId;

  // Function to set up dashboard in focused view
  const setupDashboardFocusedView = React.useCallback(
    (options: {
      dashboardId?: string;
      prompt?: string;
      context?: ContextItem[];
      autoSubmit?: boolean;
    }) => {
      const targetDashboardId =
        options.dashboardId || dashboardContext?.dashboardId;
      if (!targetDashboardId) {
        return;
      }

      PleriClient.updateChatInput({
        prompt:
          options.prompt ||
          `Working with dashboard: ${dashboardContext?.dashboardTitle || targetDashboardId}`,
        useExistingConversation: false, // Always new conversation for dashboard focused view
        context:
          options.context ||
          createWidgetContextItems(
            'Dashboard',
            'dashboard',
            undefined,
            isDashboardPage ? dashboardContext : undefined,
          ),
        autoSubmit: options.autoSubmit,
        focusedView: {
          content: (
            <UniversalDashboardRenderer
              dashboardId={targetDashboardId}
              variant="full"
              showAskPleri={false}
              dashboardTitle={dashboardContext?.dashboardTitle}
              dashboardScope={dashboardContext?.dashboardScope}
            />
          ),
          title: t('interactiveView', {
            dashboardTitle: dashboardContext?.dashboardTitle || t('dashboard'),
          }),
          printMode: 'image',
        },
      });
    },
    [dashboardContext, isDashboardPage, t],
  );

  const sendMessage = (message: string, action?: string) => {
    // Create context items using reusable function
    const contextItems = createWidgetContextItems(
      componentName,
      contextType,
      config,
      isDashboardPage ? dashboardContext : undefined,
    );

    // Add action context if provided
    if (action) {
      contextItems.push({
        id: `action-${componentName}-${Date.now()}`,
        type: 'user_action',
        prompt: `User action: ${action}`,
        display: 'Action',
      });
    }

    // If we're on a dashboard page, set up focused view with the dashboard
    if (isDashboardPage && dashboardContext?.dashboardId) {
      setupDashboardFocusedView({
        dashboardId: dashboardContext.dashboardId,
        prompt: message,
        context: contextItems,
        autoSubmit: true,
      });
    } else {
      PleriClient.updateChatInput({
        prompt: message,
        context: contextItems,
        autoSubmit: true,
        useExistingConversation: !isDashboardPage, // New conversation for dashboard pages
        appendContext: true,
      });
    }
  };

  const askQuestion = (question: string) => {
    sendMessage(question);
  };

  const requestExplanation = () => {
    sendMessage('I want to understand this chart and what it means in detail.');
  };

  const requestAnalysis = () => {
    sendMessage(
      'I want to understand the data source of where you are getting the data from for this chart',
    );
  };

  const requestRemediation = () => {
    sendMessage(
      'Help me understand what actions I should take based on this data.',
    );
  };

  const attachToChat = () => {
    // Create context items using reusable function
    const contextItems = createWidgetContextItems(
      componentName,
      contextType,
      config,
      isDashboardPage ? dashboardContext : undefined,
    );

    // If we're on a dashboard page, set up focused view with the dashboard
    if (isDashboardPage && dashboardContext?.dashboardId) {
      setupDashboardFocusedView({
        dashboardId: dashboardContext.dashboardId,
        context: contextItems,
        autoSubmit: false,
      });
    } else {
      PleriClient.updateChatInput({
        prompt: '',
        context: contextItems,
        autoSubmit: false,
        useExistingConversation: !isDashboardPage, // New conversation for dashboard pages
        appendContext: true,
      });
    }
  };

  // Helper function for external components to trigger dashboard focused view
  const openDashboardWithChat = React.useCallback(
    (dashboardId?: string, prompt?: string) => {
      setupDashboardFocusedView({
        dashboardId,
        prompt,
        autoSubmit: false, // Don't auto-submit, let user modify the prompt
      });
    },
    [setupDashboardFocusedView],
  );

  return {
    sendMessage,
    askQuestion,
    requestExplanation,
    requestAnalysis,
    requestRemediation,
    attachToChat,
    setupDashboardFocusedView,
    openDashboardWithChat,
    isDashboardPage,
    // Note: We can't determine if there's an active conversation from PleriClient
    // This would need to be handled differently if needed
    hasActiveConversation: true, // Assume there's always a way to chat
  };
};
